import { NavLink, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { demo } from "./c1lib";
import { RefreshCw, ShieldHalf } from "lucide-react";

const NAV = [
  { to: "/", label: "Overview", end: true, testid: "pubnav-overview" },
  { to: "/devotee", label: "Devotee", testid: "pubnav-devotee" },
  { to: "/temple-manager", label: "Temple Manager", testid: "pubnav-manager" },
  { to: "/validation", label: "Validation", testid: "pubnav-validation" },
  { to: "/records", label: "Verified Records", testid: "pubnav-records" },
];

export default function PublicLayout({ children }) {
  const navigate = useNavigate();
  const reset = async () => {
    try {
      const r = await demo.reset();
      toast.success(`Demo reset. ${r.deleted_journeys} journey(s) cleared; reference cases preserved.`);
      navigate("/");
    } catch {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <div className="bg-slate-900 text-slate-200 text-center text-[11px] font-mono tracking-wide py-1.5 px-4" data-testid="synthetic-banner">
        Live — first certified record: Kalabhairava Abhishekam, Hyderabad · 2 September 2026. Reference demonstration records also present.
      </div>
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4 md:gap-8">
          <Link to="/" className="shrink-0" data-testid="brand-home">
            <div className="font-serif text-2xl font-semibold tracking-tight text-slate-950 leading-none">Pramaana</div>
            <div className="hidden sm:block font-mono text-[9px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">One Sankalpa · One Verified Record</div>
          </Link>
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-sm transition-colors duration-150 ${isActive ? "text-slate-950 font-medium bg-slate-100" : "text-slate-500 hover:text-slate-900"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 ml-auto shrink-0">
            <button onClick={reset} data-testid="reset-demo-btn" aria-label="Start New Demo"
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 border border-slate-300 rounded-sm px-2.5 py-1.5 transition-colors">
              <RefreshCw size={13} /> <span className="hidden sm:inline">Start New Demo</span>
            </button>
            <Link to="/login" data-testid="founder-admin-link" aria-label="Founder / Admin"
              className="flex items-center gap-1.5 text-xs text-slate-100 bg-slate-900 hover:bg-slate-800 rounded-sm px-2.5 py-1.5 transition-colors">
              <ShieldHalf size={13} /> <span className="hidden sm:inline">Founder / Admin</span>
            </Link>
          </div>
        </div>
        <div className="md:hidden border-t border-slate-100 overflow-x-auto">
          <nav className="flex items-center gap-1 px-4 py-2 w-max">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-sm whitespace-nowrap transition-colors duration-150 ${isActive ? "text-slate-950 font-medium bg-slate-100" : "text-slate-500"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-slate-200 bg-white py-6">
        <div className="max-w-6xl mx-auto px-6 text-xs text-slate-500 font-mono flex flex-wrap gap-x-6 gap-y-1">
          <span>Pramaana — trust infrastructure for temples and devotees.</span>
          <span>Payment ≠ fulfilment. Evidence ≠ truth. Hash = record integrity, not religious proof.</span>
        </div>
      </footer>
    </div>
  );
}

export function Section({ title, subtitle, children, right }) {
  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      {(title || right) && (
        <div className="flex items-end justify-between mb-8">
          <div>
            {title && <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-950 leading-none">{title}</h1>}
            {subtitle && <p className="text-sm text-slate-500 mt-3 max-w-2xl leading-relaxed">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}
